import { Box, Flex, Text } from '@chakra-ui/react'
import { observer } from 'mobx-react-lite'

import { battingHeaders, bowlingHeaders, coreBattingHeaders, coreBowlingHeaders } from '../../data/reference'
import MatchHelpers from '../../helpers/matchHelpers'
import type { ShortScorecardProps } from '../../types/props'
import Dropdown from '../Dropdown/Dropdown'
import { ActionButtons } from './ActionButtons'
import { CurrentBatters } from './CurrentBatters'
import { CurrentBowlers } from './CurrentBowlers'
import { DataRows } from './DataRows'
import { HeaderRow } from './HeaderRow'
import { TeamSummary } from './TeamSummary'

export const ShortScorecard = observer(
  ({
    inning,
    game,
    ball,
    scoresOnly,
    mode,
    batterPerformance,
    triggerNewBatter,
    triggerNewBowler,
    setBatterPerformance,
    setBowlerPerformance,
    handlePenaltyRuns,
    isDisabled,
    editBall,
    inBall,
    insertingBall,
    closedInning,
    endOver,
  }: ShortScorecardProps) => {
    return (
      <Flex maxHeight={mode === 'advanced' ? '200px' : undefined} direction={mode === 'advanced' ? 'row' : 'column'}>
        <Box
          flex={1}
          margin={mode === 'advanced' ? '7px 0px 14px 14px' : undefined}
          padding={mode === 'advanced' ? '0 21px 0 0' : ['0', '0 7px', '0 7px', '0 7px']}
          borderRight={mode === 'advanced' ? '2px solid' : undefined}
          borderRightColor={mode === 'advanced' ? 'gray.200' : undefined}
        >
          <Box>
            {inning && <TeamSummary game={game} team={game.getBattingTeam(inning.id)} isBatting={true} />}
            {!inning && <TeamSummary game={game} team={game.getHomeTeam()} isBatting={false} />}
          </Box>
          {!scoresOnly && inning && (
            <Flex marginTop={mode === 'advanced' ? '-7px' : ['-7px', '-7px', '0', '0']}>
              <CurrentBatters
                inning={inning}
                triggerNewBatter={triggerNewBatter}
                performance={batterPerformance}
                setPerformance={setBatterPerformance}
                isDisabled={isDisabled || closedInning || endOver || insertingBall || inBall}
                editBall={editBall ? true : false}
              />
              {!isDisabled && !closedInning && !insertingBall && (
                <ActionButtons
                  type="batting"
                  mode={mode}
                  inning={inning}
                  ball={ball}
                  game={game}
                  editBall={editBall ? true : false}
                />
              )}
              {(isDisabled || closedInning || insertingBall) && <Flex flex={1.5} />}
              <Flex flex={1} direction="column">
                <HeaderRow headings={mode === 'advanced' ? battingHeaders : coreBattingHeaders} />
                {inning.battingPerformances && (
                  <DataRows
                    data={inning}
                    type="Batting"
                    cols={mode === 'advanced' ? battingHeaders : coreBattingHeaders}
                    short
                  />
                )}
              </Flex>
            </Flex>
          )}
        </Box>
        <Box
          flex={1}
          margin={mode === 'advanced' ? '7px 0px 14px 21px' : undefined}
          padding={mode === 'advanced' ? '0 21px' : ['0', '0 7px', '0 7px', '0 7px']}
          borderRight={mode === 'advanced' ? '2px solid' : undefined}
          borderRightColor={mode === 'advanced' ? 'gray.200' : undefined}
        >
          {mode === 'advanced' && (
            <Box>
              {inning && <TeamSummary game={game} team={game.getBowlingTeam(inning.id)} isBatting={false} />}
              {!inning && <TeamSummary game={game} team={game.getAwayTeam} isBatting={false} />}
            </Box>
          )}
          {!scoresOnly && inning && (
            <>
              {inning.bowlingPerformances && (
                <>
                  <Flex marginTop={mode === 'advanced' ? '-7px' : ['0', '0', '7px', '7px']}>
                    <CurrentBowlers
                      inning={inning}
                      triggerNewBowler={triggerNewBowler}
                      setPerformance={setBowlerPerformance}
                      isDisabled={isDisabled || closedInning || endOver || insertingBall || inBall}
                      editBall={editBall ? true : false}
                    />
                    {!isDisabled && !closedInning && !insertingBall && (
                      <ActionButtons type="bowling" mode={mode} inning={inning} ball={ball} game={game} />
                    )}
                    {(isDisabled || closedInning || insertingBall) && <Flex flex={1.5} />}
                    <Flex flex={1} direction="column">
                      <HeaderRow headings={mode === 'advanced' ? bowlingHeaders : coreBowlingHeaders} />
                      {inning.bowlingPerformances && (
                        <DataRows
                          data={inning}
                          type="Bowling"
                          cols={mode === 'advanced' ? bowlingHeaders : coreBowlingHeaders}
                          short
                        />
                      )}
                    </Flex>
                  </Flex>
                  <Flex marginTop={mode !== 'advanced' ? '14px' : '0px'}>
                    <Flex flex={1.25}>
                      <Text marginRight="7px" fontSize="md" as="span" display="inline">
                        Extras: {inning.getTotalExtras(game.matchConfigs.noBallRuns)}
                      </Text>
                      <Text as="span" fontSize="md" display="inline">
                        ({MatchHelpers.listExtrasFromInning(inning)} )
                      </Text>
                    </Flex>
                    <Flex flex={mode === 'advanced' ? 0.5 : 0.25} paddingLeft="11px" marginTop="-5px">
                      <Dropdown
                        target="penalty_runs"
                        options={[
                          { value: 'bat', label: '5 runs (To Batting Team)' },
                          { value: 'field', label: '5 runs (To Fielding Team)' },
                        ]}
                        value={mode === 'advanced' ? 'Penalty Runs' : 'Pen. Runs'}
                        onChange={({ value }) => {
                          if (value && handlePenaltyRuns) handlePenaltyRuns({ value })
                        }}
                        placement={mode !== 'advanced' ? 'left' : undefined}
                        isWhite
                        preserveCase
                        data-testid="penaltyRunsDropdown"
                      />
                    </Flex>
                    {mode === 'advanced' && <Flex flex={1.5} />}
                  </Flex>
                </>
              )}
            </>
          )}
        </Box>
      </Flex>
    )
  }
)
